import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { BsChatHeartFill, BsXLg, BsSendFill } from "react-icons/bs";
import useFirestoreCollection from "../utils/useFirestoreCollection";
import { useFirestoreWriter } from "../utils/useFirestoreWriter";
import "../Styles/ChatBox.css";

/* -------------------------------------------------------------------------
   The Old House — website chatbox
   - Talks to the free Cloudflare Worker (the "brain"), which proxies Gemini.
   - When the assistant confirms a booking it appends an invisible
     ```booking {...}``` block; we parse it, validate it against the same
     rules as the reservation form, and write it to Firestore the same way —
     so the admin email + dashboard keep working unchanged.
   ------------------------------------------------------------------------- */

// Set this in the website's .env file (REACT_APP_CHAT_ENDPOINT=...).
// Falls back to the local wrangler dev server for development.
const CHAT_ENDPOINT =
  process.env.REACT_APP_CHAT_ENDPOINT || "http://localhost:8787";

// Booking rules — must match src/Pages/Book.js
const OPENING_HOUR = 15;
const CLOSING_HOUR = 22;
const LEAD_MIN = 30;
const MAX_PERSONS = 50;
const MAX_NAME = 80;
const MAX_EMAIL = 120;
const ADMINS = ["p_iliakop@yahoo.gr", "mariaperou@hotmail.com"];

const WEEK_ORDER = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

// HTML escape so guest-supplied text is safe inside email markup.
const esc = (s) =>
  String(s == null ? "" : s).replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]
  );

const fmt = (d) =>
  d instanceof Date && !isNaN(d)
    ? d.toLocaleString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

function parseDateTime(s) {
  if (!s) return null;
  let str = String(s).trim().replace(" ", "T");
  if (str.length > 16) str = str.slice(0, 16);
  const d = new Date(str);
  return isNaN(d) ? null : d;
}

function toDateMaybe(v) {
  if (!v) return null;
  if (typeof v.toDate === "function") return v.toDate();
  const d = new Date(v);
  return isNaN(d) ? null : d;
}

// Extract the hidden booking block; returns { booking, text } where text is
// the reply with the block removed for display.
function extractBooking(reply) {
  const m = reply.match(/```booking\s*([\s\S]*?)```/i);
  const text = reply.replace(/```booking[\s\S]*?```/i, "").trim();
  if (!m) return { booking: null, text };
  try {
    return { booking: JSON.parse(m[1].trim()), text };
  } catch {
    return { booking: null, text };
  }
}

function validateBooking(b, closingDate, seasonClosed) {
  if (seasonClosed) return { ok: false, reason: "season" };
  const name = (b.name || "").trim();
  const email = (b.email || "").trim();
  const persons = parseInt(b.persons, 10);
  const dt = parseDateTime(b.datetime);
  if (!name || name.length > MAX_NAME) return { ok: false, reason: "name" };
  if (!email || email.length > MAX_EMAIL || !/.+@.+\..+/.test(email))
    return { ok: false, reason: "email" };
  if (!persons || persons < 1 || persons > MAX_PERSONS)
    return { ok: false, reason: "persons" };
  if (!dt) return { ok: false, reason: "datetime" };
  if (dt.getTime() < Date.now() + LEAD_MIN * 60 * 1000)
    return { ok: false, reason: "soon" };
  const h = dt.getHours();
  const min = dt.getMinutes();
  if (h < OPENING_HOUR || h > CLOSING_HOUR || (h === CLOSING_HOUR && min > 0))
    return { ok: false, reason: "hours" };
  if (closingDate && dt.getTime() > closingDate.getTime())
    return { ok: false, reason: "closing" };
  return { ok: true, name, email, persons, dt, specials: (b.special_requests || "").trim() };
}

export default function ChatBox() {
  const location = useLocation();
  const { data: hoursData } = useFirestoreCollection("hours");
  const { data: adminData } = useFirestoreCollection("administration");
  const { addDocument } = useFirestoreWriter("reservations");

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      intro: true,
      content:
        "Hello, and welcome to The Old House! I can tell you about our hours, our food, how to find us, or help you book a table. How can I help?",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [bookingDone, setBookingDone] = useState(false);

  const scrollRef = useRef(null);
  // Fires once per conversation so the team gets a single "someone's chatting"
  // heads-up email, not one per message.
  const notifiedRef = useRef(false);

  // Live info passed to the brain each turn (hours, closures, current time).
  const { liveInfo, closingDate, seasonClosed } = useMemo(() => {
    const admin = (adminData && adminData[0]) || {};
    const isWinter = !!admin.isWinter;
    const bookingsPaused = admin.bookings === false || isWinter;
    const closing = toDateMaybe(admin.closingDate);

    const now = new Date().toLocaleString("en-GB", {
      timeZone: "Europe/Athens",
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

    const sorted = [...(hoursData || [])].sort((a, b) => {
      const ia = WEEK_ORDER.indexOf(a.day);
      const ib = WEEK_ORDER.indexOf(b.day);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
    const hoursLines = sorted
      .map((d) => `- ${d.day}: ${d.isOpen ? d.opening : "Closed"}`)
      .join("\n");

    let info = `Current date & time (Athens): ${now}\n`;
    info += hoursLines
      ? `Opening hours:\n${hoursLines}\n`
      : "Opening hours: see the website.\n";
    if (isWinter)
      info +=
        "NOTE: The restaurant is currently CLOSED for the winter season — online bookings are paused.\n";
    else if (bookingsPaused)
      info += "NOTE: Online bookings are currently paused.\n";
    if (closing)
      info += `Bookings are accepted up to and including ${closing.toLocaleDateString(
        "en-GB",
        { weekday: "long", day: "numeric", month: "long", year: "numeric" }
      )}.\n`;

    return { liveInfo: info, closingDate: closing, seasonClosed: bookingsPaused };
  }, [hoursData, adminData]);

  useEffect(() => {
    if (scrollRef.current)
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  // Hide the chatbox on the admin / login screens.
  if (location.pathname.startsWith("/admin") || location.pathname.startsWith("/login"))
    return null;

  // Heads-up email to the team when a guest starts a chat. Reuses the same
  // Firebase Trigger-Email pipeline as bookings (an _emailOnly doc in the
  // "reservations" collection). Recipients are locked in firestore.rules.
  async function notifyChatStarted(firstMessage) {
    const when = new Date().toLocaleString("en-GB", {
      timeZone: "Europe/Athens",
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    const snippet = esc(String(firstMessage).slice(0, 500));
    await addDocument({
      _emailOnly: true,
      kind: "chatNotification",
      to: ["info@oldhouse.gr"],
      message: {
        subject: "Someone is chatting on the website",
        html:
          "<p>A guest just started a chat on the website.</p>" +
          "<ul><li><b>Time (Athens):</b> " + esc(when) + "</li>" +
          "<li><b>Their first message:</b> " + snippet + "</li></ul>" +
          "<p>This is an automatic heads-up — no action needed.</p>",
      },
      Date_created: new Date(),
    });
  }

  async function writeReservation(v) {
    const niceDate = fmt(v.dt);
    const specialsHtml = v.specials
      ? `<li><b>Special requests:</b> ${esc(v.specials)}</li>`
      : "";
    await addDocument({
      Name: v.name,
      Email: v.email,
      Date: v.dt,
      Date_created: new Date(),
      Persons: v.persons,
      status: "pending",
      adminNote: v.specials || "",
      source: "chatbox",
      to: ADMINS,
      message: {
        subject: "New reservation request from " + esc(v.name),
        html:
          "<p>A new reservation request (via the website chat) is waiting in the admin dashboard.</p>" +
          "<ul><li><b>Name:</b> " + esc(v.name) + "</li>" +
          "<li><b>Guests:</b> " + v.persons + "</li>" +
          "<li><b>Requested for:</b> " + esc(niceDate) + "</li>" +
          "<li><b>Customer email:</b> " + esc(v.email) + "</li>" +
          specialsHtml +
          "</ul>",
      },
    });

    await addDocument({
      _emailOnly: true,
      to: [v.email],
      message: {
        subject: "We received your reservation request",
        html:
          "<p>Hi " + esc(v.name) + ",</p>" +
          "<p>Thank you for choosing <b>The Old House</b>. " +
          "We have received your reservation request for <b>" +
          v.persons + " guest" + (v.persons > 1 ? "s" : "") +
          "</b> on <b>" + esc(niceDate) + "</b>.</p>" +
          "<p>Your request is currently <b>pending</b>. " +
          "We will email you a confirmation shortly.</p>" +
          "<p>Warm regards,<br/>The Old House team</p>",
      },
    });
  }

  async function send() {
    const text = input.trim();
    if (!text || loading) return;

    // First message of the conversation → send the team a one-time heads-up.
    // Fire-and-forget: a failed notification must never block or break the chat.
    if (!notifiedRef.current) {
      notifiedRef.current = true;
      notifyChatStarted(text).catch(() => {});
    }

    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setLoading(true);

    try {
      const apiMessages = next
        .filter((m) => !m.intro)
        .map((m) => ({ role: m.role, content: m.content }));

      const res = await fetch(CHAT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: apiMessages, context: { liveInfo } }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Request failed");

      const { booking, text: displayText } = extractBooking(data.reply || "");
      let appended = displayText || data.reply || "";

      if (booking && !bookingDone) {
        const v = validateBooking(booking, closingDate, seasonClosed);
        if (v.ok) {
          try {
            await writeReservation(v);
            setBookingDone(true);
          } catch (e) {
            appended +=
              "\n\n(Sorry — something went wrong saving that. Please try again or email info@oldhouse.gr.)";
          }
        } else {
          appended +=
            "\n\n(I couldn't complete that booking automatically — please double-check the date and time. We take bookings between 15:00 and 22:00.)";
        }
      }

      setMessages((m) => [...m, { role: "assistant", content: appended }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Sorry, I'm having trouble right now. Please try again in a moment, or email us at info@oldhouse.gr.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  }

  function onKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  return (
    <>
      <button
        className={`oh-chat__fab ${open ? "is-open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close chat" : "Chat with us"}
        title={open ? "Close chat" : "Chat with us"}
      >
        {open ? <BsXLg size={20} /> : <BsChatHeartFill size={22} />}
      </button>

      {open && (
        <section className="oh-chat__panel" aria-label="Chat with The Old House">
          <header className="oh-chat__header">
            <span className="oh-chat__title">The Old House</span>
            <span className="oh-chat__subtitle">Ask us anything · book a table</span>
            <button
              className="oh-chat__close"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
            >
              <BsXLg size={16} />
            </button>
          </header>

          <div className="oh-chat__messages" ref={scrollRef}>
            {messages.map((m, i) => (
              <div
                key={i}
                className={`oh-chat__msg oh-chat__msg--${
                  m.role === "user" ? "user" : "bot"
                }`}
              >
                {m.content}
              </div>
            ))}
            {loading && (
              <div className="oh-chat__msg oh-chat__msg--bot oh-chat__typing">
                <span></span>
                <span></span>
                <span></span>
              </div>
            )}
          </div>

          <div className="oh-chat__inputbar">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Type your message…"
              aria-label="Type your message"
              disabled={loading}
            />
            <button
              onClick={send}
              disabled={loading || !input.trim()}
              aria-label="Send message"
            >
              <BsSendFill size={16} />
            </button>
          </div>
        </section>
      )}
    </>
  );
}
